import AddIcon from '@mui/icons-material/Add';
import { Button, CircularProgress, Divider, Stack } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog, useDialogs } from '@work4all/components';
import {
  DraggableTree,
  TreeNode,
} from '@work4all/components/lib/dataDisplay/tree';
import {
  findNodeBy,
  getNodeDepth,
  insertNode,
  reindexTree,
  removeNodeBy,
  replaceNodeBy,
} from '@work4all/components/lib/dataDisplay/tree/utils';

import { useDeleteEntity } from '@work4all/data';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import {
  getCategoryFilter,
  useCategoryClasses,
} from '../../filters/use-category-class';
import { NavigationOverlayHeader } from '../../navigation-overlay-header/NavigationOverlayHeader';
import { useOrganisationAreaContext } from '../hooks';
import {
  useMutateCategories,
  useMutateParentCategories,
} from '../hooks/use-mutate-categories';
import { entityToCategoryField } from '../utils/entity-to-category-field';

import { OrganisationAreaCategoriesNewDialog } from './organisation-area-categories-new-dialog';

const MAX_DEPTH = 1;

type OrganisationAreaCategoriesPageProps = {
  setIsDirty: (state: boolean) => void;
};

export const OrganisationAreaCategoriesPage: React.FC<
  OrganisationAreaCategoriesPageProps
> = ({ setIsDirty }) => {
  const { t } = useTranslation();

  const { entity, setCategoriesEditOverlayOpen } = useOrganisationAreaContext();
  const parentCategoryField = entityToCategoryField(entity);

  const { treeData, refetch } = useCategoryClasses(getCategoryFilter(entity));

  const reIndexedTreeData = useMemo(
    () => reindexTree(treeData as TreeNode[]),
    [treeData]
  );

  const [draftTree, setDraftTree] = useState<TreeNode[]>([]);

  const isDirty = draftTree?.length > 0;
  const tree =
    !isDirty && reIndexedTreeData.length > 0 ? reIndexedTreeData : draftTree;

  const { mutateCategory: mutateParentCategories } =
    useMutateParentCategories();
  const { mutateCategory: mutateCategories } = useMutateCategories();
  const [deleteEntity] = useDeleteEntity(false);

  const [isRemoveCategoryDialogOpen, setRemoveCategoryDialogOpen] =
    useState<boolean>(false);

  const [categoryIdToRemove, setCategoryIdToRemove] = useState<{
    id: string;
    index: number;
    isParent: boolean;
  } | null>(null);
  const dialogs = useDialogs();

  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAddEditCategoryDialog = (index?: number, addNew?: boolean) => {
    const category =
      index != null && !addNew ? findNodeBy(tree, index, 'index') : undefined;
    const endIndex = draftTree[draftTree?.length - 1]?.index + 1;
    const { id } = dialogs.open(OrganisationAreaCategoriesNewDialog, {
      entity,
      category,
      usedIds: draftTree
        .filter((item) => Number(item.id) < 0)
        .map((item) => Number(item.id)),
      endIndex,
      onClose: () => dialogs.close(id),
      onConfirm: async (newCategory) => {
        let newTree = [];
        if (addNew && selected) {
          newTree = insertNode(
            tree,
            newCategory,
            selected?.id,
            selected?.index,
            false
          );
        } else {
          newTree =
            index != null
              ? replaceNodeBy(tree, newCategory, 'index')
              : [...tree, newCategory];
        }

        setDraftTree(reindexTree(newTree));
        setIsDirty(true);
        dialogs.close(id);
      },
    });
  };

  const handleRemoveCategoryDialog = (
    id: string,
    index: number,
    isParent: boolean
  ) => {
    setCategoryIdToRemove({ id, index, isParent });
    setRemoveCategoryDialogOpen(true);
  };

  const handleRemoveCategoryCancel = () => {
    setCategoryIdToRemove(null);
    setRemoveCategoryDialogOpen(false);
  };

  const handleRemoveCategory = () => {
    const updatedDraftState = reindexTree(
      removeNodeBy(tree, categoryIdToRemove.index, 'index')
    );

    setDraftTree(updatedDraftState);
    setCategoryIdToRemove(null);
    setRemoveCategoryDialogOpen(false);
    setIsDirty(true);
  };

  const handleTreeChange = (newTreeData: TreeNode[]) => {
    setDraftTree([...newTreeData]);
    setIsDirty(true);
  };

  const handleItemClick = (Category: TreeNode) => {
    setSelected(Category.id === selected?.id ? null : Category);
  };

  function getRemovedParentCategories(
    newTree: TreeNode[],
    oldTree: TreeNode[]
  ) {
    const newIds = new Set(newTree.map((item) => item.id));
    return oldTree
      .filter((item) => !newIds.has(item.id))
      .map((item) => item.id);
  }

  function getRemovedCategories(
    newTree: TreeNode[],
    oldTree: TreeNode[]
  ): string[] {
    const newMap = new Map(
      newTree.map((item) => [
        item.id,
        new Set(item.children?.map((c) => c.id) || []),
      ])
    );

    return oldTree.flatMap((oldItem) => {
      const newChildrenSet = newMap.get(oldItem.id);
      if (!newChildrenSet) return [];

      return (oldItem.children || [])
        .filter((child) => !newChildrenSet.has(child.id))
        .map((child) => child.id);
    });
  }

  function getRenamedParentCategories(
    newTree: TreeNode[],
    oldTree: TreeNode[]
  ) {
    const oldMap = new Map(oldTree.map((item) => [item.id, item.label]));
    return newTree.filter((item) => {
      const oldLabel = oldMap.get(item.id);
      return oldLabel && oldLabel !== item.label && !item.id.startsWith('-');
    });
  }

  function getRenamedCategories(
    newTree: TreeNode[],
    oldTree: TreeNode[]
  ): TreeNode[] {
    const oldChildMap = new Map(
      oldTree.flatMap(
        (parent) =>
          parent.children?.map((child) => [child.id, child.label.toString()]) ||
          []
      )
    );

    return newTree.flatMap(
      (parent) =>
        parent.children?.filter((child) => {
          const oldLabel = oldChildMap.get(child.id);
          return (
            !child.id.startsWith('-') && oldLabel && oldLabel !== child.label
          );
        }) || []
    );
  }

  async function renameParentCategories(
    newTree: TreeNode[],
    oldTree: TreeNode[]
  ) {
    const renamedParents = getRenamedParentCategories(newTree, oldTree);

    for (const parent of renamedParents) {
      await createParent(parent);
    }
  }

  async function renameCategories(newTree: TreeNode[], oldTree: TreeNode[]) {
    const renamedChildren = getRenamedCategories(newTree, oldTree);

    for (const child of renamedChildren) {
      await createChild(child);
    }
  }

  async function createNewCategories(newTree: TreeNode[]) {
    for (const item of newTree) {
      const { id, ...input } = item;
      if (!id.startsWith('-')) continue;

      // 1: create the parent category
      const newParentId = await createParent(input);

      // 2: create each child with the new parent id
      for (const child of item.children || []) {
        const { id: _id, ...input } = child;
        await createChild({ ...input, parentId: newParentId });
      }
    }
  }

  function getNewChildrenInExistingParents(
    newTree: TreeNode[],
    oldTree: TreeNode[]
  ): TreeNode[] {
    const oldMap = new Map(
      (oldTree as TreeNode[]).flatMap((parent) =>
        (parent.children || []).map((child) => [child.id, true])
      )
    );

    return newTree.flatMap((parent) =>
      (parent.children || [])
        .filter((child) => child.id.startsWith('-') && !oldMap.has(child.id))
        .map((child) => ({ ...child, parentId: Number(parent.id) }))
    );
  }

  async function createNewChildrenInExistingParents(
    newTree: TreeNode[],
    oldTree: TreeNode[]
  ) {
    const newChildrenInExistingParents = getNewChildrenInExistingParents(
      newTree,
      oldTree
    );

    for (const child of newChildrenInExistingParents) {
      await createChild(child);
    }
  }

  async function createParent(category) {
    const addId = category.id && !category.id.startsWith('-');

    const response = await mutateParentCategories({
      variables: {
        input: {
          ...(addId && { code: Number(category.id) }),
          name: category.label,
          firmenverteiler: true,
          ...parentCategoryField,
        },
      },
    });
    return response.data.upsertParentCompanyCategory.id;
  }

  async function createChild(category) {
    const addId = category.id && !category.id.startsWith('-');

    const response = await mutateCategories({
      variables: {
        input: {
          ...(addId && { code: Number(category.id) }),
          name: category.label,
          firmenverteiler: true,
          verteilerKlassenCode: category.parentId,
        },
      },
    });
    return response.data.upsertCompanyCategory.id;
  }

  const handleSubmit = async () => {
    const newTree = [...tree];
    const oldTree = [...treeData];
    const categoriesToRemove = getRemovedCategories(
      newTree,
      oldTree as TreeNode[]
    );
    const parentCategoriesToRemove = getRemovedParentCategories(
      newTree,
      oldTree as TreeNode[]
    );

    setLoading(true);

    try {
      // 1. Delete removed child categories
      if (categoriesToRemove.length) {
        await deleteEntity({
          ids: categoriesToRemove,
          type: Entities.category,
        });
      }

      // 2. Delete removed parent categories
      if (parentCategoriesToRemove.length) {
        await deleteEntity({
          ids: parentCategoriesToRemove,
          type: Entities.categoryClass,
        });
      }

      // 3. Create new parent + child categories
      await createNewChildrenInExistingParents(newTree, oldTree as TreeNode[]);

      // 4. Create new parent + child categories
      await createNewCategories(newTree);

      // 5. Update renamed parent categories
      await renameParentCategories(newTree, oldTree as TreeNode[]);

      // 6. Update renamed child categories
      await renameCategories(newTree, oldTree as TreeNode[]);

      await refetch();

      setIsDirty(false);
      setCategoriesEditOverlayOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectedItemLevel = useMemo(
    () => getNodeDepth(tree, selected?.id),
    [selected, tree]
  );

  return (
    <Stack>
      <NavigationOverlayHeader
        title={t(
          `MASK.CATEGORY_MANAGEMENT.${entity.toUpperCase()}_CATEGORIES.HEADER`
        )}
        breadcrumbsChildren={
          <Button
            size="large"
            color="primary"
            variant="contained"
            disabled={!isDirty || loading}
            startIcon={loading && <CircularProgress />}
            onClick={handleSubmit}
          >
            {t('INPUTS.SAVE')}
          </Button>
        }
      />

      <Stack flexDirection="row" alignItems="center" px="1rem" py={1}>
        <Button
          onClick={() => handleAddEditCategoryDialog(null, true)}
          startIcon={<AddIcon />}
          disabled={selectedItemLevel >= MAX_DEPTH}
        >
          {t('MASK.CATEGORY_MANAGEMENT.NEW')}
        </Button>
      </Stack>

      <Divider />

      <Stack px="1rem" py={1} gap={1}>
        <DraggableTree
          tree={tree}
          selected={selected}
          isDraggable={false}
          isSelectable={true}
          maxDepth={MAX_DEPTH}
          onChange={handleTreeChange}
          onEdit={handleAddEditCategoryDialog}
          onRemove={handleRemoveCategoryDialog}
          onItemClick={handleItemClick}
        />
      </Stack>

      <ConfirmDialog
        open={isRemoveCategoryDialogOpen}
        title={t(
          `MASK.CATEGORY_MANAGEMENT.${entity.toUpperCase()}_CATEGORIES.REMOVE.HEADER`
        )}
        description={
          categoryIdToRemove?.isParent
            ? t('MASK.CATEGORY_MANAGEMENT.REMOVE.DESCRIPTION_SUBCATEGORIES')
            : t('MASK.CATEGORY_MANAGEMENT.REMOVE.DESCRIPTION_CATEGORY')
        }
        confirmLabel={t('ALERTS.YES')}
        cancelLabel={t('ALERTS.NO')}
        onConfirm={handleRemoveCategory}
        onCancel={handleRemoveCategoryCancel}
      />
    </Stack>
  );
};
